/**
 * Which "9am" someone meant.
 *
 * An hour without a zone is not a time, and a "9am" that quietly means UTC is
 * worse than not offering the choice: a seller in California gets their morning
 * digest at 1am and concludes the setting is broken. So every stored delivery
 * hour travels with an IANA zone name, and this module is where that name is
 * obtained and checked.
 *
 * Detected, never asked. Nobody wants to answer a timezone question in order to
 * pick a delivery time, and the browser already knows the answer with more
 * precision than a dropdown would get out of them.
 */

/**
 * `digest_timezone` is a `varchar`-shaped slot inside a JSON blob, and this is the
 * cap the schema has always applied. The longest name the IANA database actually
 * ships is well under half of it ("America/Argentina/ComodRivadavia", 31), so the
 * limit only ever fires on something that was never a zone name.
 */
export const MAX_TIMEZONE_LENGTH = 64;

/**
 * Is this a zone name the runtime can actually resolve?
 *
 * `Intl` throws a RangeError for an unrecognised zone, which is the only real
 * validator available — there is no list to check against, and the set changes
 * with the ICU version underneath. Checking here rather than at send time is the
 * point: an unresolvable zone discovered inside a cron costs the reader the hour
 * they picked, silently, months later.
 *
 * Both `Intl.DateTimeFormat` and the wider `Intl` object are guarded because this
 * runs in browsers as well as on the server.
 */
export function isValidTimezone(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const name = value.trim();
  if (!name || name.length > MAX_TIMEZONE_LENGTH) return false;

  try {
    // Constructing is what validates; the formatter itself is discarded.
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/**
 * The reader's own zone, straight from the browser, or null if it will not say.
 *
 * `resolvedOptions().timeZone` is the browser's own answer to "where is this
 * person", derived from the OS rather than from an IP address, so it survives a
 * VPN and a datacentre round trip in a way a geo lookup does not. It is a plain
 * read of already-resolved state — no permission prompt, nothing to consent to.
 *
 * Null is returned rather than a guess. A caller that cannot get a zone should
 * fall back deliberately (see `getVercelGeo().timezone`) rather than inherit
 * whatever this function felt like inventing.
 */
export function browserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(zone) ? zone : null;
  } catch {
    return null;
  }
}
